import type { Rank } from "@rivaled/shared";
import { RankIcon, rankColors, rankVar, rankVarAlpha } from "./RankIcon";

export type RankProgressBarProps = {
  rank: Rank;
  nextRank: Rank | null;
  nextRankName: string | null;
  progressPercent: number;
  eloToNext: number;
  currentThreshold: number;
  nextThreshold: number;
  className?: string;
};

export function RankProgressBar({
  rank,
  nextRank,
  nextRankName,
  progressPercent,
  eloToNext,
  currentThreshold,
  nextThreshold,
  className,
}: RankProgressBarProps) {
  if (!nextRank) {
    return (
      <div
        className={`relative flex items-center gap-3 px-3 py-2.5 rounded-lg border overflow-hidden ${className ?? ""}`}
        style={{
          borderColor: rankVarAlpha(rank, 30),
          background: `linear-gradient(135deg, ${rankVarAlpha(rank, 12)}, ${rankVarAlpha(rank, 5)})`,
          boxShadow: `0 0 12px ${rankVarAlpha(rank, 10)}, inset 0 1px 0 ${rankVarAlpha(rank, 10)}`,
        }}
      >
        {/* Label */}
        <p
          className="relative text-[11px] font-display font-bold uppercase tracking-widest shrink-0"
          style={{ color: rankVar(rank) }}
        >
          Max Rank Achieved
        </p>

        {/* Full progress bar at 100% with shimmer */}
        <div
          className="relative flex-1 h-2 rounded-full overflow-hidden"
          style={{ backgroundColor: rankVarAlpha(rank, 15) }}
        >
          <div
            className="h-full w-full rounded-full relative overflow-hidden"
            style={{ backgroundColor: rankVar(rank) }}
          >
            <div
              className="absolute inset-0 animate-shimmer"
              style={{
                backgroundImage:
                  "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)",
                backgroundSize: "200% 100%",
              }}
            />
          </div>
          {/* Underglow */}
          <div
            className="absolute -bottom-1 inset-x-0 h-2 rounded-full blur-md"
            style={{ backgroundColor: rankVar(rank), opacity: 0.5 }}
          />
        </div>
      </div>
    );
  }

  const barWidth = `${Math.min(Math.max(progressPercent, 3), 100)}%`;
  const gradient = `linear-gradient(90deg, ${rankVar(rank)}, ${rankVar(nextRank)})`;

  return (
    <div className={className}>
      <div className="flex items-center gap-2.5 md:-mt-4">
        <div className="flex-1 relative">
          <div className="h-2.5 bg-surface-700/50 rounded-full overflow-hidden border border-surface-600/20">
            <div
              className="h-full rounded-full transition-all duration-700 ease-out relative overflow-hidden"
              style={{ width: barWidth, background: gradient }}
            >
              <div
                className="absolute inset-0 animate-shimmer"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%)",
                  backgroundSize: "200% 100%",
                }}
              />
            </div>
          </div>
          <div
            className="absolute -bottom-1 left-0 h-2 rounded-full blur-md transition-all duration-700"
            style={{ width: barWidth, background: gradient, opacity: 0.35 }}
          />
        </div>
        <RankIcon rank={nextRank} size="md" showLevels={false} />
      </div>
      <div className="flex items-start gap-2.5 -mt-0.5">
        <div className="flex-1 flex items-center justify-between">
          <span className="text-2xs text-surface-500 tabular-nums font-medium leading-none">
            {currentThreshold}
          </span>
          <span className="text-2xs text-surface-400 leading-none">
            <span className="text-white font-display font-bold tabular-nums">{eloToNext}</span> to{" "}
            <span className={`font-display font-bold ${rankColors[nextRank].text}`}>
              {nextRankName}
            </span>
          </span>
        </div>
        <span className="text-2xs text-surface-500 tabular-nums font-medium text-center w-12 shrink-0 leading-none">
          {nextThreshold}
        </span>
      </div>
    </div>
  );
}
